import {
  BarChartOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FallOutlined,
  FileDoneOutlined,
} from '@/components/Icons';
import { absoluteServerPath, CDN2, resolveConfigUrl } from '@/helpers/api';
import { cacheFetch } from '@/helpers/cacheFetch';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import React, { Suspense } from 'react';
import Search from '@components2/Search';
import { GetServerSideProps } from 'next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip } from '@fortawesome/free-solid-svg-icons';
import ModalDownloadApp from '@/components2/ModalDownloadApp';
import { basicPageProps, mobileProps } from '@/utils/isServer';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { isMobile, isIos } = mobileProps(ctx);
  const { configInfo, airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
      mobileType: isIos ? 'ios' : 'android',
    },
  };
};

export default function Airports({
  airports,
  deviceType,
  mobileType,
  ...props
}) {
  return (
    <div className="">
      <Head>
        <title>Discount Cruise Parking | On Air Parking</title>
        <meta
          name="description"
          content="We provide the lowest rates with five star facilities. 24/7 Free shuttles and free cancellations are always included. Book now!"
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/Cruise"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Discount Airport Parking | On Air Parking<"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We are in 25+ locations in the United States with On Air Parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <Suspense>
        <div className="block sm:block md:min-h-full relative">
          <Image
            src={CDN2() + '/staticmyapp/bg-search-airport.svg'}
            fill
            style={{ objectFit: 'cover' }}
            priority
            className="z-0"
            alt="Airport Background"
          />
          <div className="container mx-auto px-4 py-8 relative z-10">
            <h2 className="mb-2 text-2xl px-5 uppercase flex flex-row items-center w-full">
              <FontAwesomeIcon
                className="text-brown font-bold text-5xl"
                icon={faShip}
                {...props}
                style={{ width: 39, height: 25 }}
              />
              <span className="ml-1 text-brown">Cruise</span>
            </h2>
            <Search searchType="cruise" airports={airports} deviceType={deviceType} />
          </div>
        </div>
      </Suspense>
      <Suspense>
        <div className="container mx-auto px-4 mt-8 md:mt-16 max-w-4xl">
          <h2 className="text-2xl font-bold text-center mb-12">
            Cruise We Serve
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 mb-8">
            <div className="flex flex-row">
              <EnvironmentOutlined className="text-primary font-bold text-5xl mr-8" />
              <p className="text-base">
                Discover affordable & convenient cruise parking deals!
              </p>
            </div>
            <div className="flex flex-row">
              <DollarOutlined className="text-primary font-bold text-5xl mr-8" />
              <p className="text-base">
                Lowest price at five-star cruise parking facilities, minutes
                from the port - we're always finding ways to save you more!
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24">
            <div className="flex flex-row">
              <BarChartOutlined className="text-primary font-bold text-5xl mr-8" />
              <p className="text-base self-center">
                Personalized customer support.
              </p>
            </div>

            <div className="flex flex-row">
              <FileDoneOutlined className="text-primary font-bold text-5xl mr-8" />

              <p className="text-base self-center">
                Complimentary shuttles* and free cancellation up to start date.
              </p>
            </div>
          </div>
        </div>
      </Suspense>
      <Suspense>
        <h2 className="text-2xl font-bold text-center my-12">Ports</h2>
        <div className="mx-auto container px-4 mb-12">
          <div className="flex flex-row flex-wrap">
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Maryland-Cruise-Terminal-Parking"
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Baltimore Cruise Port"
              >
                <Image
                  src={CDN2() + '/staticmyapp/maryland-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Baltimore Cruise Port"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Maryland Cruise Terminal
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Port-Everglades-EVG"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port Everglades"
              >
                <Image
                  src={CDN2() + '/staticmyapp/everglades-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port Everglades"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port Everglades
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Jacksonville-Port-JXP"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port of Jacksonville"
              >
                <Image
                  src={CDN2() + '/staticmyapp/jacksonville-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port of Jacksonville"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port of Jacksonville
                </h3>
              </Link>
            </div>
          </div>

          <div className="flex flex-row flex-wrap">
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Miami-Port-MIA"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port of Miami"
              >
                <Image
                  src={CDN2() + '/staticmyapp/miami-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port of Miami"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port of Miami
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Port-Norfolk-Virginia"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port of Norfolk"
              >
                <Image
                  src={CDN2() + '/staticmyapp/norfolk-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port of Norfolk"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port of Norfolk
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Tampa-Port-TMP"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port of Tampa"
              >
                <Image
                  src={CDN2() + '/staticmyapp/tampa-port.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port of Tampa"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port of Tampa
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/port-of-san-diego-SANCP"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Port of San Diego"
              >
                <Image
                  src={CDN2() + '/staticmyapp/san-diego.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Port of San Diego"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Port of San Diego
                </h3>
              </Link>
            </div>
            <div className="w-full md:w-1/3 h-64 mb-4 px-2">
              <Link
                href="/parking/Port-Canaveral-PCAN"
                passHref
                className="relative flex flex-col items-center justify-center w-full h-full bg-grey-dark bg-no-repeat bg-center bg-cover opacity-80 transform hover:scale-95 transition"
                title="View Cape Canaveral"
              >
                <Image
                  src={CDN2() + '/staticmyapp/cape-canaveral.webp'}
                  fill
                  style={{ objectFit: 'cover' }}
                  quality={75}
                  className="z-0"
                  sizes="100vw"
                  alt="Cape Canaveral"
                />
                <h3 className="font-bold text-white text-xl z-10 filter drop-shadow-lg ">
                  Cape Canaveral
                </h3>
              </Link>
            </div>
          </div>
          <p className="text-xs self-center">
            *Select locations may not include a free shuttle
          </p>
        </div>
      </Suspense>
    </div>
  );
}
